import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import { postListExcerpt } from "../../utils/blogExcerpt";
import { getDateLocale } from "../../utils/locale";
import "../../components/blog/blog.css";

import { BLOG_API as API } from "../../config/api";

function dateParts(post) {
    const d = new Date(post.published_at || post.created_at);
    return {
        day: d.getDate(),
        month: d.toLocaleDateString(getDateLocale(), { month: "short" }),
        year: d.getFullYear(),
    };
}

function formatSidebarDate(post) {
    return new Date(post.published_at || post.created_at).toLocaleDateString(
        getDateLocale(),
        {
            year: "numeric",
            month: "long",
            day: "numeric",
        },
    );
}

function BlogArchivePager({ page, totalPages, setPage, idPrefix }) {
    const { t } = useTranslation();
    if (totalPages <= 1) return null;
    return (
        <nav className="blog-archive-pager" aria-label={t("blog.paginationAria")}>
            <div className="blog-archive-pager-inner" id={idPrefix || "pager"}>
                <button
                    type="button"
                    className="blog-pager-nav"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label={t("blog.prevPageAria")}
                >
                    «
                </button>
                {Array.from({ length: totalPages }).map((_, i) => {
                    const n = i + 1;
                    return (
                        <button
                            key={n}
                            type="button"
                            className={`blog-pager-num ${page === n ? "is-current" : ""}`}
                            onClick={() => setPage(n)}
                            aria-label={t("blog.pageAria", { n })}
                            aria-current={page === n ? "page" : undefined}
                        >
                            {n}
                        </button>
                    );
                })}
                <button
                    type="button"
                    className="blog-pager-nav"
                    disabled={page === totalPages}
                    onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                    }
                    aria-label={t("blog.nextPageAria")}
                >
                    »
                </button>
            </div>
        </nav>
    );
}

function Blog() {
    const { t } = useTranslation();
    const [posts, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [archiveFilter, setArchiveFilter] = useState(null);
    const [archives, setArchives] = useState([]);
    const [recentPosts, setRecentPosts] = useState([]);
    const [sidebarReady, setSidebarReady] = useState(false);
    const [archiveMonthPosts, setArchiveMonthPosts] = useState([]);
    const [archiveMonthLoading, setArchiveMonthLoading] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (debouncedQ) setArchiveFilter(null);
    }, [debouncedQ]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQ, archiveFilter]);

    useEffect(() => {
        let cancelled = false;
        const loadSidebar = async () => {
            try {
                const [archRes, recentRes] = await Promise.all([
                    axios.get(`${API}/archives/`),
                    axios.get(`${API}/posts/`, { params: { page: 1 } }),
                ]);
                if (cancelled) return;
                setArchives(Array.isArray(archRes.data) ? archRes.data : []);
                const raw = recentRes.data.results || recentRes.data || [];
                setRecentPosts(Array.isArray(raw) ? raw.slice(0, 5) : []);
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setSidebarReady(true);
            }
        };
        loadSidebar();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!archiveFilter) {
            setArchiveMonthPosts([]);
            return;
        }
        let cancelled = false;
        const loadMonthPosts = async () => {
            setArchiveMonthLoading(true);
            try {
                const res = await axios.get(`${API}/posts/`, {
                    params: {
                        year: archiveFilter.year,
                        month: archiveFilter.month,
                        page: 1,
                        page_size: 80,
                    },
                });
                if (cancelled) return;
                const raw = res.data.results || res.data || [];
                setArchiveMonthPosts(Array.isArray(raw) ? raw : []);
            } catch (e) {
                if (!cancelled) setArchiveMonthPosts([]);
            } finally {
                if (!cancelled) setArchiveMonthLoading(false);
            }
        };
        loadMonthPosts();
        return () => {
            cancelled = true;
        };
    }, [archiveFilter]);

    useEffect(() => {
        const fetchPosts = async () => {
            setPostsLoading(true);
            try {
                const params = {
                    page,
                    ...(debouncedQ ? { q: debouncedQ } : {}),
                    ...(archiveFilter
                        ? {
                              year: archiveFilter.year,
                              month: archiveFilter.month,
                          }
                        : {}),
                };
                const response = await axios.get(`${API}/posts/`, { params });
                const raw = response.data.results || response.data;
                const list = Array.isArray(raw) ? raw : [];
                setPosts(list);
                const count =
                    typeof response.data.count === "number"
                        ? response.data.count
                        : list.length;
                setTotalCount(count);
                setTotalPages(Math.max(1, Math.ceil(count / 9)));
            } catch (error) {
                console.error("Error fetching posts:", error);
            } finally {
                setPostsLoading(false);
            }
        };

        fetchPosts();
    }, [page, debouncedQ, archiveFilter]);

    const clearSearch = useCallback(() => {
        setSearchInput("");
        setDebouncedQ("");
    }, []);

    const selectArchiveMonth = useCallback((item) => {
        setSearchInput("");
        setDebouncedQ("");
        setArchiveFilter({
            year: item.year,
            month: item.month,
            label: item.label,
        });
    }, []);

    const clearArchiveFilter = useCallback(() => {
        setArchiveFilter(null);
    }, []);

    return (
        <Layouts>
            <header className="shrink-0">
                <NavbarComponent />
            </header>

            <main className="blog-page">
                <div className="blog-container blog-container--wide">
                    <div className="blog-header">
                        <h1 className="blog-title">{t("blog.title")}</h1>
                        <p className="blog-subtitle">
                            {t("blog.subtitle")}
                        </p>
                    </div>

                    <div className="blog-toolbar">
                            <div className="blog-toolbar-meta">
                                <span className="blog-toolbar-count">
                                    {totalCount === 1
                                        ? t("blog.articleCountOne")
                                        : t("blog.articleCountOther", { count: totalCount })}
                                </span>
                                {archiveFilter ? (
                                    <span className="blog-toolbar-chip">
                                        {t("blog.archiveLabel", { label: archiveFilter.label })}
                                        <button
                                            type="button"
                                            className="blog-toolbar-chip-x"
                                            onClick={clearArchiveFilter}
                                            aria-label={t("blog.removeArchiveFilterAria")}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ) : null}
                                {debouncedQ ? (
                                    <button
                                        type="button"
                                        className="blog-toolbar-clear"
                                        onClick={clearSearch}
                                    >
                                        {t("blog.clearSearch")}
                                    </button>
                                ) : null}
                            </div>
                            <label className="blog-search-label">
                                <span className="sr-only">
                                    {t("blog.searchSr")}
                                </span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className="blog-search-icon"
                                    aria-hidden
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <input
                                    type="search"
                                    className="blog-search-input"
                                    placeholder={t("blog.searchPlaceholder")}
                                    value={searchInput}
                                    onChange={(e) =>
                                        setSearchInput(e.target.value)
                                    }
                                    autoComplete="off"
                                />
                            </label>
                        </div>

                    <div className="blog-layout-cols">
                        <div className="blog-layout-primary">
                            {postsLoading ? (
                                <div className="blog-loading blog-loading--primary">
                                    <div className="spinner"></div>
                                </div>
                            ) : posts.length > 0 ? (
                                    <>
                                        <div
                                            className={`blog-archive-wrap ${totalPages > 1 ? "has-pager" : ""}`}
                                        >
                                            <BlogArchivePager
                                                page={page}
                                                totalPages={totalPages}
                                                setPage={setPage}
                                                idPrefix="blog-pager-top"
                                            />

                                            <div className="blog-archive-main">
                                                <ul className="blog-archive-list">
                                                    {posts.map((post) => {
                                                        const {
                                                            day,
                                                            month,
                                                            year,
                                                        } = dateParts(post);
                                                        const excerpt =
                                                            postListExcerpt(
                                                                post,
                                                            );
                                                        return (
                                                            <li
                                                                key={
                                                                    post.id
                                                                }
                                                                className="blog-archive-item"
                                                            >
                                                                <article className="blog-archive-entry">
                                                                    <div
                                                                        className="blog-archive-entry-date"
                                                                        aria-hidden
                                                                    >
                                                                        <span className="blog-archive-day">
                                                                            {
                                                                                day
                                                                            }
                                                                        </span>
                                                                        <span className="blog-archive-month">
                                                                            {
                                                                                month
                                                                            }
                                                                        </span>
                                                                        <span className="blog-archive-year">
                                                                            {
                                                                                year
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div className="blog-archive-entry-body">
                                                                        <h2 className="blog-archive-entry-title">
                                                                            <Link
                                                                                to={`/blog/${post.slug}`}
                                                                            >
                                                                                {
                                                                                    post.title
                                                                                }
                                                                            </Link>
                                                                        </h2>
                                                                        <div className="blog-archive-entry-content-row">
                                                                            {post.featured_image_url ? (
                                                                                <Link
                                                                                    to={`/blog/${post.slug}`}
                                                                                    className="blog-archive-entry-thumb-link"
                                                                                >
                                                                                    <img
                                                                                        src={
                                                                                            post.featured_image_url
                                                                                        }
                                                                                        alt=""
                                                                                        className="blog-archive-entry-thumb"
                                                                                    />
                                                                                </Link>
                                                                            ) : null}
                                                                            <p className="blog-archive-entry-excerpt">
                                                                                {excerpt ||
                                                                                    t("blog.noExcerpt")}
                                                                            </p>
                                                                        </div>
                                                                        <Link
                                                                            to={`/blog/${post.slug}`}
                                                                            className="blog-archive-readmore"
                                                                        >
                                                                            {t("blog.readFullArticle")}
                                                                            <span aria-hidden>
                                                                                {" "}
                                                                                →
                                                                            </span>
                                                                        </Link>
                                                                    </div>
                                                                </article>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>

                                        {totalPages > 1 ? (
                                            <div className="blog-pager-bottom-wrap">
                                                <p className="blog-pager-bottom-hint">
                                                    {t("blog.morePages")}
                                                </p>
                                                <BlogArchivePager
                                                    page={page}
                                                    totalPages={totalPages}
                                                    setPage={setPage}
                                                    idPrefix="blog-pager-bottom"
                                                />
                                            </div>
                                        ) : null}
                                    </>
                            ) : (
                                    <div className="blog-empty-state blog-empty-state--inline">
                                        <p className="blog-empty-title">
                                            {debouncedQ
                                                ? t("blog.noResultsSearch")
                                                : archiveFilter
                                                  ? t("blog.noEntriesMonth")
                                                  : t("blog.noPostsYet")}
                                        </p>
                                        <p className="blog-empty-text">
                                            {debouncedQ
                                                ? t("blog.tryOtherWords")
                                                : archiveFilter
                                                  ? t("blog.tryOtherMonth")
                                                  : t("blog.whenPublished")}
                                        </p>
                                        {debouncedQ ? (
                                            <button
                                                type="button"
                                                className="blog-empty-btn"
                                                onClick={clearSearch}
                                            >
                                                {t("blog.viewAllArticles")}
                                            </button>
                                        ) : null}
                                        {archiveFilter && !debouncedQ ? (
                                            <button
                                                type="button"
                                                className="blog-empty-btn"
                                                onClick={clearArchiveFilter}
                                            >
                                                {t("blog.viewAllDates")}
                                            </button>
                                        ) : null}
                                    </div>
                            )}
                        </div>

                            <aside
                                className="blog-sidebar-right"
                                aria-label={t("blog.sidebarAria")}
                            >
                                <div className="blog-widget">
                                    <h2 className="blog-widget-title">
                                        {t("blog.recentPosts")}
                                    </h2>
                                    {!sidebarReady ? (
                                        <p className="blog-widget-muted">
                                            {t("blog.loading")}
                                        </p>
                                    ) : recentPosts.length === 0 ? (
                                        <p className="blog-widget-muted">
                                            {t("blog.noEntriesYet")}
                                        </p>
                                    ) : (
                                        <ul className="blog-recent-list">
                                            {recentPosts.map((rp) => (
                                                <li
                                                    key={rp.id}
                                                    className="blog-recent-item"
                                                >
                                                    <Link
                                                        to={`/blog/${rp.slug}`}
                                                        className="blog-recent-link"
                                                    >
                                                        {rp.featured_image_url ? (
                                                            <span className="blog-recent-thumb-wrap">
                                                                <img
                                                                    src={
                                                                        rp.featured_image_url
                                                                    }
                                                                    alt=""
                                                                    className="blog-recent-thumb"
                                                                />
                                                            </span>
                                                        ) : (
                                                            <span className="blog-recent-thumb-wrap blog-recent-thumb-wrap--empty" />
                                                        )}
                                                        <span className="blog-recent-text">
                                                            <span className="blog-recent-title">
                                                                {rp.title}
                                                            </span>
                                                            <span className="blog-recent-date">
                                                                {formatSidebarDate(
                                                                    rp,
                                                                )}
                                                            </span>
                                                        </span>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="blog-widget">
                                    <h2 className="blog-widget-title">
                                        {t("blog.archive")}
                                    </h2>
                                    {!sidebarReady ? (
                                        <p className="blog-widget-muted">
                                            {t("blog.loading")}
                                        </p>
                                    ) : archives.length === 0 ? (
                                        <p className="blog-widget-muted">
                                            {t("blog.noMonthsYet")}
                                        </p>
                                    ) : (
                                        <ul className="blog-archive-month-list">
                                            {archives.map((a) => {
                                                const active =
                                                    archiveFilter &&
                                                    archiveFilter.year ===
                                                        a.year &&
                                                    archiveFilter.month ===
                                                        a.month;
                                                return (
                                                    <li
                                                        key={`${a.year}-${a.month}`}
                                                        className={
                                                            active
                                                                ? "blog-archive-month-li is-expanded"
                                                                : "blog-archive-month-li"
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`blog-archive-month-btn ${active ? "is-active" : ""}`}
                                                            onClick={() =>
                                                                selectArchiveMonth(
                                                                    a,
                                                                )
                                                            }
                                                        >
                                                            <span className="blog-archive-month-label">
                                                                {a.label}
                                                            </span>
                                                            <span className="blog-archive-month-count">
                                                                ({a.count})
                                                            </span>
                                                        </button>
                                                        {active ? (
                                                            <div
                                                                className="blog-archive-month-entries"
                                                                role="region"
                                                                aria-label={t("blog.entriesOf", { label: a.label })}
                                                            >
                                                                {archiveMonthLoading ? (
                                                                    <p className="blog-widget-muted blog-archive-month-entries-loading">
                                                                        {t("blog.loadingEntries")}
                                                                    </p>
                                                                ) : archiveMonthPosts.length ===
                                                                  0 ? (
                                                                    <p className="blog-widget-muted">
                                                                        {t("blog.noEntries")}
                                                                    </p>
                                                                ) : (
                                                                    <>
                                                                        <ol className="blog-archive-entry-links">
                                                                            {archiveMonthPosts.map(
                                                                                (
                                                                                    p,
                                                                                ) => (
                                                                                    <li
                                                                                        key={
                                                                                            p.id
                                                                                        }
                                                                                    >
                                                                                        <Link
                                                                                            to={`/blog/${p.slug}`}
                                                                                            className="blog-archive-entry-link"
                                                                                        >
                                                                                            {
                                                                                                p.title
                                                                                            }
                                                                                        </Link>
                                                                                    </li>
                                                                                ),
                                                                            )}
                                                                        </ol>
                                                                        {a.count >
                                                                            archiveMonthPosts.length && (
                                                                            <p className="blog-archive-month-more">
                                                                                {t("blog.moreInMainList", {
                                                                                    count:
                                                                                        a.count -
                                                                                        archiveMonthPosts.length,
                                                                                })}
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        ) : null}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </aside>
                    </div>
                </div>
            </main>

            <Footer />
        </Layouts>
    );
}

export default Blog;
